import { Component, ErrorHandler, Input, OnChanges, OnDestroy, OnInit, ChangeDetectionStrategy, signal } from '@angular/core'
import { FieldTree, form, validate, ValidationError } from '@angular/forms/signals'
import { NumberDate } from '@lemon/core/model/common'
import { DateService } from '@lemon/core/service/date.service'
import { FirebaseLemonator } from 'app/_angular/service/firebase-lemonator.service'
import { asetaArvo, nollaa } from 'app/_jaettu-angular/_lomakkeet/arvon-muutokset'
import { Asiakas, AsiakasTagi, AsiakkaanTagi } from 'app/_jaettu-lemonator/model/asiakas'
import { YllapitoUriService } from 'app/_jaettu-lemonator/service/yllapito-uri.service'
import { Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'

interface NaytettavaAsiakkaanTagi {
  lahde: AsiakkaanTagi
  nimi: string
  pvmTeksti: string
  varikoodi: string
  taustavari: string
}

interface TagiLomake {
  tagi: string | null
  alkuPvm: Date | null
  loppuPvm: Date | null
}

interface TaginJaksoLomake {
  alkuPvm: Date | null
  loppuPvm: Date | null
}

const TYHJA_TAGILOMAKE: TagiLomake = { tagi: null, alkuPvm: null, loppuPvm: null }
const TYHJA_JAKSOLOMAKE: TaginJaksoLomake = { alkuPvm: null, loppuPvm: null }

@Component({
  selector: 'app-asiakas-tagit',
  templateUrl: './asiakas-tagit.component.html',
  styleUrls: ['./asiakas-tagit.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsiakasTagitComponent implements OnInit, OnChanges, OnDestroy {
  @Input() asiakas: Asiakas

  private _ngUnsubscribe = new Subject<void>()
  private _tagiMaaritykset: AsiakasTagi[] = []

  readonly asiakkaanTagit = signal<NaytettavaAsiakkaanTagi[]>([])
  readonly paattyneetTagit = signal<NaytettavaAsiakkaanTagi[]>([])
  readonly valittavatTagit = signal<AsiakasTagi[]>([])
  naytaPaattyneet = false

  lisaysAuki = false
  tagiLomake: FieldTree<TagiLomake>

  muokattavaTagi: NaytettavaAsiakkaanTagi = null
  muokataanPaattynytta = false
  muokkausLomake: FieldTree<TaginJaksoLomake>

  constructor(
    private _errorHandler: ErrorHandler,
    private _firebaseLemonator: FirebaseLemonator,
    private _yllapitoUriService: YllapitoUriService,
    private _dateService: DateService,
  ) {
    this.tagiLomake = form(signal(TYHJA_TAGILOMAKE), polku => {
      validate(polku.loppuPvm, ({ value, valueOf }) => this._loppuEnnenAlkua(valueOf(polku.alkuPvm), value()))
      validate(polku.loppuPvm, ({ value, valueOf }) => this._paallekkainenJakso(valueOf(polku.tagi), valueOf(polku.alkuPvm), value()))
    })
    this.muokkausLomake = form(signal(TYHJA_JAKSOLOMAKE), polku => {
      validate(polku.loppuPvm, ({ value, valueOf }) => this._loppuEnnenAlkua(valueOf(polku.alkuPvm), value()))
      validate(polku.loppuPvm, ({ value, valueOf }) => this._muokattavanJaksonPaallekkaisyys(valueOf(polku.alkuPvm), value()))
    })
  }

  ngOnInit() {
    this._firebaseLemonator
      .firestoreCollection<AsiakasTagi>(this._yllapitoUriService.annaAsiakasTagitCollectionUri())
      .listen()
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe({
        next: tagiMaaritykset => {
          this._tagiMaaritykset = (tagiMaaritykset || []).sort((a, b) => a.nimi.localeCompare(b.nimi))
          this._paivitaNaytettavatTagilistat()
        },
        error: err => this._errorHandler.handleError(err),
      })
  }

  ngOnChanges() {
    this._paivitaNaytettavatTagilistat()
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  lisaaTagi() {
    const tagiAvain = this.tagiLomake.tagi().value()
    const pvm = this.tagiLomake.alkuPvm().value()
    if (!this.asiakas || !tagiAvain || !pvm) {
      return
    }

    const loppuPvm = this.tagiLomake.loppuPvm().value()
    if (this.tagiLomake().invalid()) {
      this.tagiLomake().markAsTouched()
      return
    }

    const uusi: AsiakkaanTagi = { avain: tagiAvain, alkuPvm: this._dateService.dateToNumber(pvm) }
    if (loppuPvm) {
      uusi.loppuPvm = this._dateService.dateToNumber(loppuPvm)
    }
    const uudetTagit = [...(this.asiakas.tagit ?? []), uusi]

    this.peruutaValinta()
    this._asetaTagit(uudetTagit)
  }

  avaaLisays() {
    this.lisaysAuki = true
    asetaArvo(this.tagiLomake.alkuPvm, new Date())
  }

  peruutaValinta() {
    this.lisaysAuki = false
    nollaa(this.tagiLomake, TYHJA_TAGILOMAKE)
  }

  vaihdaPaattyneidenNakyvyys() {
    this.naytaPaattyneet = !this.naytaPaattyneet
    if (!this.naytaPaattyneet && this.muokataanPaattynytta) {
      this.peruutaMuokkaus()
    }
  }

  vaihdaMuokkaus(tagi: NaytettavaAsiakkaanTagi) {
    const samaTagiOnJoMuokattavana = this.muokattavaTagi && this._onSamaJakso(this.muokattavaTagi.lahde, tagi.lahde)
    if (samaTagiOnJoMuokattavana) {
      this.peruutaMuokkaus()
      return
    }

    this.muokattavaTagi = tagi
    this.muokataanPaattynytta = this.paattyneetTagit().includes(tagi)
    nollaa(this.muokkausLomake, {
      alkuPvm: this._dateService.numberToDate(tagi.lahde.alkuPvm),
      loppuPvm: tagi.lahde.loppuPvm ? this._dateService.numberToDate(tagi.lahde.loppuPvm) : null,
    })
  }

  peruutaMuokkaus() {
    this.muokattavaTagi = null
    this.muokataanPaattynytta = false
    nollaa(this.muokkausLomake, TYHJA_JAKSOLOMAKE)
  }

  valitseMuokkaus() {
    const alkuPvm = this.muokkausLomake.alkuPvm().value()
    if (!this.muokattavaTagi || !alkuPvm) {
      return
    }

    const loppuPvm = this.muokkausLomake.loppuPvm().value()
    if (this.muokkausLomake().invalid()) {
      this.muokkausLomake().markAsTouched()
      return
    }

    const vanhaJakso = this.muokattavaTagi.lahde
    const muokattuJakso: AsiakkaanTagi = { avain: vanhaJakso.avain, alkuPvm: this._dateService.dateToNumber(alkuPvm) }
    if (loppuPvm) {
      muokattuJakso.loppuPvm = this._dateService.dateToNumber(loppuPvm)
    }
    const uudetTagit = (this.asiakas.tagit ?? []).map(jakso => (this._onSamaJakso(jakso, vanhaJakso) ? muokattuJakso : jakso))

    this.peruutaMuokkaus()
    this._asetaTagit(uudetTagit)
  }

  poistaJakso(tagi: NaytettavaAsiakkaanTagi) {
    const uudetTagit = (this.asiakas.tagit ?? []).filter(jakso => !this._onSamaJakso(jakso, tagi.lahde))

    this.peruutaMuokkaus()
    this._asetaTagit(uudetTagit)
  }

  private _asetaTagit(uudetTagit: AsiakkaanTagi[]) {
    this.asiakas.tagit = uudetTagit
    this._paivitaNaytettavatTagilistat()
  }

  private _paivitaNaytettavatTagilistat() {
    const tanaan = this._dateService.currentNumberDate()
    const kaikki = this.asiakas?.tagit || []
    const aktiiviset = kaikki.filter(tagi => !tagi.loppuPvm || tagi.loppuPvm >= tanaan)
    const paattyneet = kaikki.filter(tagi => tagi.loppuPvm && tagi.loppuPvm < tanaan)

    this.asiakkaanTagit.set(aktiiviset.map(tagi => this._muodostaNaytettavaTagi(tagi)))
    this.paattyneetTagit.set(paattyneet.map(tagi => this._muodostaNaytettavaTagi(tagi)))
    this.valittavatTagit.set(this._tagiMaaritykset.filter(maaritys => !maaritys.poistettu))
  }

  private _muodostaNaytettavaTagi(tagi: AsiakkaanTagi): NaytettavaAsiakkaanTagi {
    const maaritys = this._tagiMaaritykset.find(tagiMaaritys => tagiMaaritys.avain === tagi.avain)
    const alkuTeksti = this._dateService.muotoileNumberPaiva(tagi.alkuPvm, 'fi')
    const pvmTeksti = tagi.loppuPvm ? alkuTeksti + ' – ' + this._dateService.muotoileNumberPaiva(tagi.loppuPvm, 'fi') : alkuTeksti + '–'
    return {
      lahde: tagi,
      nimi: maaritys?.nimi ?? 'Tuntematon tagi',
      pvmTeksti: pvmTeksti,
      varikoodi: maaritys?.varit?.varikoodi ?? '#5f7182',
      taustavari: maaritys?.varit?.taustavari ?? '#e8edf1',
    }
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string) {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }

  private _paallekkainenJakso(tagiAvain: string, alkuPvm: Date, loppuPvm: Date): ValidationError | null {
    if (!alkuPvm || !tagiAvain) {
      return null
    }
    return this._tarkistaPaallekkaisyys(tagiAvain, alkuPvm, loppuPvm, null)
  }

  private _muokattavanJaksonPaallekkaisyys(alkuPvm: Date, loppuPvm: Date): ValidationError | null {
    if (!alkuPvm || !this.muokattavaTagi) {
      return null
    }
    return this._tarkistaPaallekkaisyys(this.muokattavaTagi.lahde.avain, alkuPvm, loppuPvm, this.muokattavaTagi.lahde)
  }

  private _loppuEnnenAlkua(alkuPvm: Date, loppuPvm: Date): ValidationError | null {
    if (!loppuPvm || !alkuPvm) {
      return null
    }
    const loppuEnnenAlkua = loppuPvm.getTime() < alkuPvm.getTime()
    return loppuEnnenAlkua ? { kind: 'loppuEnnenAlkua' } : null
  }

  private _tarkistaPaallekkaisyys(tagiAvain: string, alkuPvm: Date, loppuPvm: Date | null, ohitettavaJakso: AsiakkaanTagi | null): ValidationError | null {
    const uusiAlku = this._dateService.dateToNumber(alkuPvm)
    const uusiLoppu = loppuPvm ? this._dateService.dateToNumber(loppuPvm) : null

    const aiemmatJaksot = (this.asiakas?.tagit || []).filter(tagi => tagi.avain === tagiAvain && !(ohitettavaJakso && this._onSamaJakso(tagi, ohitettavaJakso)))
    const meneePaallekkain = aiemmatJaksot.some(vanhaJakso => this._menevatkoJaksotPaallekkain(uusiAlku, uusiLoppu, vanhaJakso))
    return meneePaallekkain ? { kind: 'paallekkain' } : null
  }

  private _onSamaJakso(a: AsiakkaanTagi, b: AsiakkaanTagi): boolean {
    return a.avain === b.avain && a.alkuPvm === b.alkuPvm
  }

  private _menevatkoJaksotPaallekkain(uusiAlku: NumberDate, uusiLoppu: NumberDate | null, vanhaJakso: AsiakkaanTagi): boolean {
    const alkaaVanhanJaksonPaatyttya = vanhaJakso.loppuPvm && uusiAlku > vanhaJakso.loppuPvm
    if (alkaaVanhanJaksonPaatyttya) {
      return false
    }
    const paattyyEnnenVanhanJaksonAlkua = uusiLoppu && uusiLoppu < vanhaJakso.alkuPvm
    if (paattyyEnnenVanhanJaksonAlkua) {
      return false
    }
    return true
  }
}
